/**
 * v3.15-1168 — The Platform (Enterprise Positioning pass).
 *
 * Sells the operating system — not feature checklists.
 * Every section answers: "Why should I trust these people with my clients?"
 */
import { Link } from 'react-router-dom';
import { ExternalLink, ArrowRight, Users, Eye, CheckCircle2, Briefcase, Banknote, Building2, Truck, Landmark, FileText, ShieldCheck } from 'lucide-react';
import PublicLayout from '../components/PublicLayout';
import EndPageCTA from '../components/EndPageCTA';
import EcosystemGraphic from '../components/EcosystemGraphic';
import GhostLogo from '../components/GhostLogo';

const BUSINESS_PRODUCTS = [
  { Icon: Banknote,   title: 'Working Capital',         body: 'Short-term liquidity to keep operations moving.' },
  { Icon: FileText,   title: 'Business Term Loans',     body: 'Structured installment financing for growth initiatives.' },
  { Icon: Truck,      title: 'Equipment Financing',     body: 'Acquire or replace equipment without depleting reserves.' },
  { Icon: Briefcase,  title: 'Lines of Credit',         body: 'Flexible draw access for cash-flow timing gaps.' },
  { Icon: Building2,  title: 'Revenue-Based Financing', body: 'Capital that flexes with monthly receipts.' },
  { Icon: Landmark,   title: 'SBA Options',             body: 'Government-backed programs for qualifying businesses.' },
];

const PlatformPage = () => (
  <PublicLayout testId="platform-page">

    {/* HERO */}
    <section className="bg-primary text-white px-6 py-32 lg:py-44">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-8">The Platform</p>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-8">
          One platform. One team.<br />
          <span className="text-gradient-gold">Three financial solutions.</span>
        </h1>
        <p className="text-xl sm:text-2xl text-slate-300 font-light max-w-3xl leading-relaxed">
          Prestigious Hub is the financial operations platform our partners run their business on — orchestrated by a human Application Concierge and accelerated by Ghost Intelligence™, our AI operations assistant.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/platform-tour" className="inline-flex items-center gap-2 bg-gradient-gold text-slate-900 font-semibold px-6 py-3 rounded hover:shadow-gold transition-all" data-testid="platform-hero-tour">
            Take the Platform Tour <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/ghost" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white font-semibold px-6 py-3 rounded hover:bg-white/20 transition-all" data-testid="platform-hero-ghost">
            <GhostLogo size="xs" /> Meet Ghost<sup className="text-[8px]">™</sup>
          </Link>
        </div>
      </div>
    </section>

    {/* OUTCOME — sell the operating system, not features */}
    <section className="bg-white px-6 py-28 lg:py-36">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">What partners actually buy</p>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.05] mb-10">
          Our partners don&rsquo;t hire processors.<br />
          <span className="text-slate-600">They hire us.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 text-lg text-slate-700 leading-relaxed">
          <p><strong className="text-slate-900">Our Concierge handles the work.</strong> One named human per client, from submission to funding. You stop being the bottleneck.</p>
          <p><strong className="text-slate-900">Our technology keeps everyone informed.</strong> The platform is the source of truth. No status calls, no reconciliation meetings.</p>
          <p><strong className="text-slate-900">Ghost keeps everything connected.</strong> Our AI operations assistant monitors files 24/7, flags risk, drafts communication, and never goes home.</p>
          <p><strong className="text-slate-900">Our platform becomes an extension of your business.</strong> You bring the client. We finish the deal. You keep the relationship — and the profit.</p>
        </div>
      </div>
    </section>

    {/* Ecosystem graphic */}
    <section className="bg-slate-50 px-6 py-24 lg:py-28">
      <div className="max-w-5xl mx-auto">
        <EcosystemGraphic testId="platform-ecosystem-graphic" />
      </div>
    </section>

    {/* THREE FINANCIAL SOLUTIONS — full enterprise explanations */}
    <section className="bg-white px-6 py-28 lg:py-36" id="consumer">
      <div className="max-w-6xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Solution 1 · Consumer Funding</p>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold text-primary leading-[1.1] mb-6">
          Consumer Funding through EasyQuickFund™
        </h2>
        <p className="text-lg text-slate-600 max-w-3xl leading-relaxed mb-10">
          <strong className="text-slate-900">EasyQuickFund</strong> is the consumer funding brand inside the Prestigious Companies network. Built for fast, low-friction personal financing — most options begin with a soft inquiry, so clients explore real offers without affecting their credit.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { t: 'Soft-pull prequalification', b: 'See realistic offers before any hard inquiry.' },
            { t: 'No hard inquiry to explore', b: 'Zero credit impact during discovery.' },
            { t: 'Financing up to $100,000', b: 'Personal loans for qualifying borrowers.' },
            { t: '~150 lending partners', b: 'A growing nationwide lender network.' },
          ].map((c) => (
            <div key={c.t} className="bg-slate-50 border border-slate-100 rounded-lg p-5">
              <CheckCircle2 className="h-5 w-5 text-primary mb-3" />
              <p className="font-heading text-sm font-bold text-slate-900 mb-1">{c.t}</p>
              <p className="text-xs text-slate-600 leading-relaxed">{c.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-slate-50 px-6 py-28 lg:py-36" id="business">
      <div className="max-w-6xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Solution 2 · Business Funding</p>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold text-primary leading-[1.1] mb-6">
          Commercial capital, end-to-end inside the Hub.
        </h2>
        <p className="text-lg text-slate-600 max-w-3xl leading-relaxed mb-10">
          One application. One Concierge. One platform. Multiple funding solutions — your business clients get the same Concierge experience as the consumer flow, with the full menu of commercial capital products at their disposal.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {BUSINESS_PRODUCTS.map(({ Icon, title, body }) => (
            <div
              key={title}
              className="bg-white border border-slate-100 rounded-lg p-5 hover:border-primary/30 hover:shadow-soft transition-all"
              data-testid={`bizfund-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <Icon className="h-5 w-5 text-primary mb-3" />
              <p className="font-heading text-sm font-bold text-slate-900 mb-1">{title}</p>
              <p className="text-xs text-slate-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-white px-6 py-28 lg:py-36" id="cf360">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Solution 3 · Credit Improvement</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-primary leading-[1.1] mb-6">
            CreditForward360
          </h2>
          <a
            href="https://creditforward360.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-6"
            data-testid="platform-cf360-link"
          >
            creditforward360.com <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <div className="lg:col-span-7">
          <p className="text-lg text-slate-700 leading-relaxed mb-4">
            <strong>CreditForward360 helps clients who aren&rsquo;t fundable today become fundable tomorrow</strong> — through personalized credit improvement plans designed specifically for future financing.
          </p>
          <p className="text-base text-slate-600 leading-relaxed mb-4">
            When a client cannot be funded right away, the Concierge routes them into CreditForward360 instead of letting them walk away. Structured remediation, tracked progress, and a return path back into the funding workflow when ready.
          </p>
          <p className="text-base text-slate-600 leading-relaxed">
            A &ldquo;not yet&rdquo; never becomes a permanent &ldquo;no&rdquo; — and the partner who introduced the client keeps the relationship intact.
          </p>
        </div>
      </div>
    </section>

    {/* PROOF — large dominant screenshots */}
    <section className="bg-slate-50 px-6 py-28 lg:py-36">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-4">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Real-time visibility</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-primary leading-[1.1] mb-6">
            Your dashboard.<br />Your business. Live.
          </h2>
          <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
            Every active file, every stage, every funding event — visible the moment it happens. Inside the Hub, you never have to make a status call.
          </p>
        </div>
        <div className="lg:col-span-8">
          <figure className="rounded-xl shadow-2xl ring-1 ring-slate-200 overflow-hidden bg-white" data-testid="hub-shot-dashboard-lg">
            <img src="/marketing/hub-dashboard.jpg" alt="Prestigious Hub — Partner Dashboard" className="w-full h-auto block" loading="lazy" />
            <figcaption className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-400 border-t border-slate-100">
              Partner Dashboard · Live screenshot
            </figcaption>
          </figure>
        </div>
      </div>
    </section>

    {/* WHY TRUST US — the three pillars (no SaaS feature grid) */}
    <section className="bg-white px-6 py-28 lg:py-36">
      <div className="max-w-6xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3 text-center">Why partners trust us with their clients</p>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary text-center leading-[1.1] mb-16">
          Human. Technology. Visibility.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="border-t-2 border-gold pt-6" data-testid="pillar-human">
            <Users className="h-7 w-7 text-primary mb-4" />
            <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">The Concierge</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              One named Application Concierge per client. End-to-end accountability from submission through funding. They live in the Hub alongside you — same files, same conversation, same outcome.
            </p>
          </div>
          <div className="border-t-2 border-gold pt-6" data-testid="pillar-ai">
            <GhostLogo size="md" className="mb-4" />
            <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">Ghost<sup className="text-[10px]">™</sup></h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Our AI Operations Assistant — think Copilot for funding. Monitors every file in real time, flags risk, surfaces the next best action, and drafts communication so deals never stall.{' '}
              <Link to="/ghost" className="text-primary hover:underline" data-testid="platform-pillar-ghost-link">Learn more →</Link>
            </p>
          </div>
          <div className="border-t-2 border-gold pt-6" data-testid="pillar-visibility">
            <Eye className="h-7 w-7 text-primary mb-4" />
            <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">Total Transparency</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              You see everything your Concierge sees. Every document, every lender response, every status change. No black box. No status calls. Just the truth, refreshed live.
            </p>
          </div>
        </div>
        <div className="mt-12 flex items-center justify-center gap-3 text-sm text-slate-500">
          <ShieldCheck className="h-4 w-4 text-primary" />
          ACH disbursement standard. Same-day wire on qualified files. Full audit trail on every funded deal.
        </div>
      </div>
    </section>

    {/* Closing reinforcement */}
    <section className="bg-gradient-to-b from-slate-900 to-primary text-white px-6 py-28 lg:py-36">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-8">
          One platform. One team.<br />
          <span className="text-gradient-gold">Three financial solutions.</span>
        </h2>
        <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed mb-10">
          Consumer Funding (EasyQuickFund™) · Business Funding · Credit Improvement (CreditForward360).
          One Concierge per file. Ghost Intelligence™ on every deal.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/platform-tour"
            className="inline-flex items-center gap-2 bg-gradient-gold text-slate-900 font-semibold px-6 py-3 rounded hover:shadow-gold transition-all"
            data-testid="platform-cta-tour"
          >
            Take the Platform Tour
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/presentation"
            className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white font-semibold px-6 py-3 rounded hover:bg-white/20 transition-all"
            data-testid="platform-cta-presentation"
          >
            View the Partner Presentation
          </Link>
        </div>
      </div>
    </section>

    <EndPageCTA testId="platform-end-cta" />
  </PublicLayout>
);

export default PlatformPage;
